/**
 * Reglas de los documentos de la empresa que no dependen de Angular.
 *
 * `requiredDocumentsFor` es la única fuente de «qué hace falta para ofertar».
 * La leen el bloqueo de aceptar/rechazar ofertas (`DocumentsService`) y el
 * resumen de Ajustes › Documentos: si cada uno llevara su lista, un día el
 * resumen diría «al día» a alguien que no puede ofertar.
 */

export type RequiredDocument = {
  label: string;
  /** Basta con uno de estos tipos para darlo por cubierto. */
  anyOf: string[];
};

export function requiredDocumentsFor(role: string | undefined): RequiredDocument[] {
  if (role === 'shipper') return [];
  const required: RequiredDocument[] = [
    { label: 'Constancia de situación fiscal', anyOf: ['tax_constancy'] },
  ];
  if (role !== 'carrier') {
    required.push({ label: 'Términos y condiciones', anyOf: ['default_t&c', 'terms_and_conditions'] });
  }
  return required;
}

export function missingRequiredDocuments(role: string | undefined, types: string[]): RequiredDocument[] {
  return requiredDocumentsFor(role).filter(doc => !doc.anyOf.some(type => types.includes(type)));
}

/**
 * La opinión de cumplimiento del SAT vale 30 días naturales **desde su
 * emisión**, no desde que se sube: una emitida hace 25 días y subida hoy vence
 * en 5. Por eso se pide la fecha de emisión y el vencimiento se deduce.
 */
export const OPINION_VALIDITY_DAYS = 30;

/** `YYYY-MM-DD` → mediodía local, para que ni la zona horaria ni el cambio de horario muevan el día. */
export function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  return isNaN(date.getTime()) ? null : date;
}

export function toDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function opinionExpiry(issuedAt: Date): Date {
  const expiry = new Date(issuedAt);
  expiry.setDate(expiry.getDate() + OPINION_VALIDITY_DAYS);
  return expiry;
}

/** Vencida cuando el día de vencimiento ya pasó; el propio día todavía vale. */
export function isExpired(expirationDate: number | string | Date | undefined | null, now: Date = new Date()): boolean {
  if (expirationDate === undefined || expirationDate === null || expirationDate === '') return false;
  const expiry = new Date(expirationDate);
  if (isNaN(expiry.getTime())) return false;
  const endOfExpiryDay = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate(), 23, 59, 59, 999);
  return now.getTime() > endOfExpiryDay.getTime();
}

/**
 * El tipo de un documento libre. Antes era `company-<nombre sin espacios>`, así
 * que dos documentos con el mismo nombre caían en el mismo grupo del backend y
 * el segundo se guardaba como versión del primero. El sufijo lo hace único; el
 * prefijo se conserva porque es por lo que la vista los lista.
 */
export function freeDocumentType(prefix: 'company' | 'certificate', name: string, now: number = Date.now()): string {
  const slug = (name ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${prefix}-${slug || 'documento'}-${now.toString(36)}`;
}

/**
 * La lista del backend trae **cada versión** de un grupo como un documento
 * aparte cuando el nombre del archivo cambia. Solo cuenta la última —es la que
 * el expediente de un flete toma—, así que la vista enseña una por tipo.
 */
export function latestPerType<T extends { typeDocument?: string; version?: number }>(docs: T[]): T[] {
  const latest = new Map<string, T>();
  for (const doc of docs) {
    const key = doc.typeDocument ?? '';
    const current = latest.get(key);
    if (!current || (doc.version ?? 0) > (current.version ?? 0)) latest.set(key, doc);
  }
  return [...latest.values()];
}
